export const getNextSiblings = (elem: Node | HTMLElement | null): Node[] => {
  if (!elem) return [];
  const sibs = [elem];
  while (elem && elem.nextSibling) {
    elem = elem.nextSibling;
    if (elem.nodeType === 3) continue; // text node
    sibs.push(elem);
  }
  return sibs;
};

const QUOTE_CLASS = 'dixa-collapsed-quote';
const SHOW_QUOTE_BUTTON_CLASS = 'dixa-show-quote-button';
const DIXA_QUOTE_CLASS = 'dixa_quote';

const addShowQuotesButton = (
  quoteNode: Element,
  documentToSelectFrom: Document | ShadowRoot,
): void => {
  const showButton = document.createElement('button');
  showButton.className = SHOW_QUOTE_BUTTON_CLASS;
  showButton.innerHTML = '&hellip;';
  Object.assign(showButton.style, {
    outline: '0',
    background: 'none',
    border: '1px solid #dcdcdc',
    borderRadius: '4px',
    fontSize: '18px',
    margin: '8px 0 0 0',
    cursor: 'pointer',
  });
  showButton.addEventListener('click', () => {
    // Show quotes
    documentToSelectFrom
      .querySelectorAll(`.${QUOTE_CLASS}`)
      .forEach((quote) => quote.classList.add('show'));
    // Hide buttons
    documentToSelectFrom
      .querySelectorAll(`.${SHOW_QUOTE_BUTTON_CLASS}`)
      .forEach((btn) => btn.classList.add('hidden'));
  });
  quoteNode.before(showButton);
};

/**
 * Finds all quotes within a document, minimizes them and adds a button for
 * uncollapsing them.
 */
export function minimizeQuotes(
  document: Document,
  documentToSelectFrom: Document | ShadowRoot,
): void {
  QUOTE_SELECTORS.forEach((quoteSelector) => {
    const QuotesNodeList = documentToSelectFrom.querySelectorAll(quoteSelector);
    QuotesNodeList.forEach((quoteNode) => {
      // Marking the node as quote, so that it can be expanded/collapsed using CSS
      quoteNode.classList.add(QUOTE_CLASS);
      addShowQuotesButton(quoteNode, documentToSelectFrom);
    });
  });
}

export const QUOTE_SELECTORS = [
  'div.gmail_quote',
  'div.yahoo_quoted',
  `.${DIXA_QUOTE_CLASS}`,
  'spark_quote',
  'div.moz-cite-prefix',
  'blockquote',
];

export const OUTLOOK_QUOTES_STYLES = [
  'border:none;border-top:solid #E1E1E1 1.0pt;padding:3.0pt 0', // Outlook 2013  (american) <color=#E1E1E1> <unit=pt>
  'border:none;border-top:solid #E1E1E1 1.0cm;padding:3.0cm 0', // Outlook 2013 (international) <color=#E1E1E1> <unit=cm>
  'border:none;border-top:solid #B5C4DF 1.0pt;padding:3.0pt 0', // Outlook 2007, 2010 (american) <color=#B5C4DF> <unit=pt>
  'border:none;border-top:solid #B5C4DF 1.0cm;padding:3.0cm 0', // Outlook 2007, 2010 (international) <color=#B5C4DF> <unit=cm>
  'padding-top: 5px; border-top-color: rgb(229, 229, 229); border-top-width: 1px; border-top-style: solid;', // Windows Mailer
];

// Wrap wrapper around nodes
// Just pass a collection of nodes, and a wrapper element
const wrapAll = (nextElements: Node[], wrapper: HTMLElement): HTMLElement => {
  // Cache the current parent and previous sibling of the first node.
  const parent = nextElements[0].parentNode;
  let previousSibling = nextElements[0].previousSibling;
  if (!previousSibling) {
    previousSibling = document.createElement('div');
    parent?.insertBefore(previousSibling, nextElements[0]);
  }

  // Place each node in wrapper.
  //  - If nodes is an array, we must increment the index we grab from
  //    after each loop.
  //  - If nodes is a NodeList, each node is automatically removed from
  //    the NodeList when it is removed from its parent with appendChild.

  // @FIXME there is a known bug here.
  // https://dixa-product.atlassian.net/browse/TEAMS-1747
  // If the document contains two html/body tags,
  // it will break the iframe.document.body and set it to null.
  for (
    let i = 0;
    nextElements.length - i;
    wrapper.firstChild === nextElements[0] && i++
  ) {
    wrapper.appendChild(nextElements[i]);
  }

  // Place the wrapper just after the cached previousSibling
  if (wrapper && previousSibling) {
    parent?.insertBefore(wrapper, previousSibling.nextSibling);
  }

  return wrapper;
};

export const wrapElementsInDixaQuote = (elements: Node[]): void => {
  const wrapper = document.createElement('div');
  wrapper.className = DIXA_QUOTE_CLASS;

  wrapAll(elements, wrapper);
};
